// src/entitySelectionPrompts.ts
var entitySelectionPrompts = [
  {
    content: `How often is the <strong>{ENTITY}</strong> used on a daily and weekly basis?`
  },
  {
    content: `What are the peak usage hours for the <strong>{ENTITY}</strong> throughout the day?`
  },
  {
    content: `How does the usage frequency for the <strong>{ENTITY}</strong> vary on weekdays versus weekends?`
  },
  {
    content: `Which days of the week see the highest and lowest usage for this <strong>{ENTITY}</strong>?`
  },
  {
    content: `Is the <strong>{ENTITY}</strong> underutilized during certain time slots? If so, which times?`
  }
];

// src/excludedTypes.ts
var excludedTypes = [
  "asset",
  "Asset",
  "NFArea",
  "wall",
  "Wall",
  "WALL",
  "operator",
  "Operator"
];

// src/loadingMessageData.ts
var loadingMessageData = [
  "Fetching Digital Twin",
  "Analysing Areas and Assets",
  "Rendering 3D Digital Twin"
];

// src/onboardingSteps.tsx
import { PanelAnchorPos } from "@repo/frontend-types";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var onboardingSteps = [
  {
    header: "Switch between twins",
    copy: /* @__PURE__ */ jsx("p", { children: "Click the TwinLabs logo to open your twin selection page. There you can see all the Twins you have access to." }),
    anchorPos: PanelAnchorPos.TOP_LEFT,
    posX: 60,
    posY: 50,
    pointerPos: 15
  },
  {
    header: "Choose the asset",
    copy: /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("p", { children: "All the \u201Cthings\u201D you can see in this panel we call Assets, and this is the Asset Panel." }),
      /* @__PURE__ */ jsx("p", { children: "Click the Asset Panel to open your twin hierarchy and assets. It helps you monitor and manage assets in Live Mode (real-time data) and Historical Mode." }),
      /* @__PURE__ */ jsx("p", { children: "Click categories to expand and drill down, hover to highlight assets in the 3D environment, and click to select and view details." }),
      /* @__PURE__ */ jsx("p", { children: "Dots represent performance, with colour intensity showing usage, and warnings flagging issues." }),
      /* @__PURE__ */ jsx("p", { children: "Grey-striped assets show there is no data being collected for that asset." }),
      /* @__PURE__ */ jsx("p", { children: "Selecting an asset filters all the data on the screen." })
    ] }),
    anchorPos: PanelAnchorPos.TOP_LEFT,
    posX: 310,
    posY: -3,
    pointerPos: 14
  },
  {
    header: "Select an asset in the 3D environment",
    copy: /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("p", { children: "This scene shows your digital twin in 3D coloured to reflect your data." }),
      /* @__PURE__ */ jsx("p", { children: "Just like the Asset Panel, hovering over any asset lets you preview the details." }),
      /* @__PURE__ */ jsx("p", { children: "Clicking on an asset selects it and shows you all the data for that asset and filters all the data on screen to that selected asset." }),
      /* @__PURE__ */ jsx("p", { children: "In Live Mode you see real-time insights, whereas in Historical Mode you are seeing historical trends." }),
      /* @__PURE__ */ jsx("p", { children: "Striped assets show there is no data being collected for that asset." })
    ] }),
    anchorPos: PanelAnchorPos.CENTRE,
    posX: -500,
    posY: -300,
    pointerPos: 4
  },
  {
    header: "Navigating the 3D scene",
    copy: /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("p", { children: "Orbit: Right-click and drag to rotate around your 3D twin. Alternatively, hold the Control (Ctrl) key and right-click to orbit." }),
      /* @__PURE__ */ jsx("p", { children: "Pan: Left-click and drag to pan the view." }),
      /* @__PURE__ */ jsx("p", { children: "Zoom: use the scroll wheel (scroll up to zoom in, down to zoom out), or hold \u2318 / Ctrl while scrolling for zoom." })
    ] }),
    anchorPos: PanelAnchorPos.BOTTOM_CENTRE,
    posX: 0,
    posY: -500,
    pointerPos: 9
  },
  {
    header: "Find support",
    copy: /* @__PURE__ */ jsx("p", { children: "Our AI assistant Maria helps you navigate the TwinLabs platform by providing quick answers and guidance. Press the Maria button, ask a question, and get step-by-step instructions on using features and tools. Whether you need help with navigation, settings, or understanding data, Maria is there to assist." }),
    anchorPos: PanelAnchorPos.TOP_RIGHT,
    posX: -34,
    posY: 80,
    pointerPos: 2
  },
  {
    header: "Expand the view",
    copy: /* @__PURE__ */ jsx("p", { children: "Fullscreen Toggle: This enables you to enter and exit the Fullscreen view, which hides all user interface (UI) elements to give you a more immersive view of the 3D scene. Great for showing the boss!" }),
    anchorPos: PanelAnchorPos.TOP_RIGHT,
    posX: -160,
    posY: 80,
    pointerPos: 2
  },
  {
    header: "Switch mode",
    copy: /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("p", { children: "Live Mode shows real-time data, continuously updating as events happen." }),
      /* @__PURE__ */ jsx("p", { children: "Now, click on the mode dropdown to select Historical Mode to continue." }),
      /* @__PURE__ */ jsx("p", { children: "Historical Mode lets you explore historical data. In this mode, the Calendar opens automatically, allowing you to filter and review past trends." }),
      /* @__PURE__ */ jsx("p", { children: 'There is a "Data last updated at" timestamp indicating when the data was last refreshed, just in case you leave the screen untouched for a long time.' })
    ] }),
    anchorPos: PanelAnchorPos.TOP_RIGHT,
    posX: -450,
    posY: 80,
    pointerPos: 3,
    cardTop: 46
  },
  {
    header: "Explore Trends",
    copy: /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("p", { children: "The Calendar lets you explore historical data by selecting specific dates or time periods." }),
      /* @__PURE__ */ jsx("p", { children: "Click on individual dates or drag to select a range. You can drag, by selecting the top left of a cell then dragging to select the date or time range while still holding down your left mouse button." }),
      /* @__PURE__ */ jsx("p", { children: "Selecting dates filters all the data you're seeing, allowing you to focus on specific trends." }),
      /* @__PURE__ */ jsx("p", { children: "Deselect by clicking on a selected date again." })
    ] }),
    anchorPos: PanelAnchorPos.CENTRE_RIGHT,
    posX: -430,
    posY: 250,
    pointerPos: 4
  },
  {
    header: "Switch calendars",
    copy: /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("p", { children: "The Calendar Switcher lets you toggle between Monthly and Weekly views, allowing you to choose between a broad overview or detailed daily data." }),
      /* @__PURE__ */ jsx("p", { children: "Click Monthly for long-term trends or Weekly for a closer look at specific days and hours." })
    ] }),
    anchorPos: PanelAnchorPos.CENTRE_RIGHT,
    posX: -430,
    posY: -3,
    pointerPos: 4
  },
  {
    header: "Filter data",
    copy: /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("p", { children: "The Filters display any active filters for assets, date, and times." }),
      /* @__PURE__ */ jsx("p", { children: "Selecting anything - an asset in the Asset Panel on in the 3D scene, or some dates or times in the calendar - automatically filters all the data on the screen and adds a a filter here. Clicking the cross on a filter removes it instantly." }),
      /* @__PURE__ */ jsx("p", { children: "Using Clear All removes all filters at once." }),
      /* @__PURE__ */ jsx("p", { children: "That's it! You're now ready to explore and interact with your digital twin." })
    ] }),
    anchorPos: PanelAnchorPos.TOP_CENTRE,
    posX: 0,
    posY: 130,
    pointerPos: 1
  }
];

// src/stackedChartColors.ts
var getStackedChartColors = [
  {
    fill: "#51C4E4",
    lineColor: "#75BFFF"
  },
  {
    fill: "#66AEEC",
    lineColor: "#6BBBFF"
  },
  {
    fill: "#7599F3",
    lineColor: "#5CB4FF"
  },
  {
    fill: "#7C82FB",
    lineColor: "#47AAFF"
  },
  {
    fill: "#8368FE",
    lineColor: "#47AAFF"
  },
  {
    fill: "#7A4DFE",
    lineColor: "#6BBBFF"
  }
];

// src/weekDays.ts
var weekDays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday"
];

// src/fluent2/figmaVariables.ts
var figmaVariables = {
  colors: {
    grey: {
      black: "#000000",
      2: "#050505",
      4: "#0A0A0A",
      6: "#0F0F0F",
      8: "#141414",
      10: "#1A1A1A",
      12: "#1F1F1F",
      14: "#242424",
      16: "#292929",
      18: "#2E2E2E",
      20: "#333333",
      22: "#383838",
      24: "#3D3D3D",
      26: "#424242",
      28: "#474747",
      30: "#4D4D4D",
      32: "#525252",
      34: "#575757",
      36: "#5C5C5C",
      38: "#616161",
      40: "#666666",
      42: "#6B6B6B",
      44: "#707070",
      46: "#757575",
      48: "#7A7A7A",
      50: "#808080",
      52: "#858585",
      54: "#8A8A8A",
      56: "#8F8F8F",
      58: "#949494",
      60: "#999999",
      62: "#9E9E9E",
      64: "#A3A3A3",
      66: "#A8A8A8",
      68: "#ADADAD",
      70: "#B3B3B3",
      72: "#B8B8B8",
      74: "#BDBDBD",
      76: "#C2C2C2",
      78: "#C7C7C7",
      80: "#CCCCCC",
      82: "#D1D1D1",
      84: "#D6D6D6",
      86: "#DBDBDB",
      88: "#E0E0E0",
      90: "#E6E6E6",
      92: "#EBEBEB",
      94: "#F0F0F0",
      96: "#F5F5F5",
      98: "#FAFAFA",
      white: "#FFFFFF",
      white40: "rgba(255, 255, 255, 0.4)",
      white60: "rgba(255, 255, 255, 0.6)"
    },
    brand: {
      10: "#001615",
      20: "#002E2F",
      30: "#004549",
      40: "#005D63",
      50: "#00747E",
      60: "#008C98",
      70: "#00A3B2",
      80: "#00BBCC",
      90: "#18C2D1",
      100: "#30C9D6",
      110: "#47D0DA",
      120: "#5FD8DF",
      130: "#77DFE4",
      140: "#8FE6E8",
      150: "#A6EDED",
      160: "#BEF4F2"
    },
    graph: {
      10: "#7A4DFE",
      20: "#8368FE",
      30: "#7C82FB",
      40: "#7599F3",
      50: "#66AEEC",
      60: "#51C4E4"
    }
  },
  cornerRadius: {
    none: 0,
    20: 2,
    40: 4,
    60: 6,
    80: 8,
    120: 12,
    circular: 99
  },
  size: {
    none: 0,
    20: 2,
    40: 4,
    60: 6,
    80: 8,
    120: 12,
    160: 12,
    200: 12,
    240: 12,
    280: 12,
    320: 12,
    360: 12,
    400: 12,
    480: 12,
    520: 12,
    560: 12
  },
  strokeWidth: {
    none: 0,
    5: 0.5,
    10: 1,
    15: 1.5,
    20: 2,
    30: 3,
    40: 4,
    60: 6
  }
};

// src/fluent2/dataColors.ts
var dataColors = {
  // N/A
  na: {
    color: figmaVariables.colors.grey.white40
  },
  // 0%
  zero: {
    backgroundColor: figmaVariables.colors.grey[98]
    // 0%
  },
  // 1-25%
  low: {
    backgroundColor: figmaVariables.colors.brand[160]
    // 1-25%
  },
  // 26-50%
  medium: {
    backgroundColor: figmaVariables.colors.brand[140]
    // 26-50%
  },
  // 51-100%
  high: {
    backgroundColor: figmaVariables.colors.brand[120]
    // 51-100%
  },
  // +100%
  veryHigh: {
    backgroundColor: "#C50F1F"
    // +100%
  }
};

// src/fluent2/lightTheme.ts
var lightTheme = {
  // - - - - - - - - - - - - - - - -
  // - - - - - - Neutral - - - - - -
  // - - - - - - - - - - - - - - - -
  // Neutral / Background / 1
  colorNeutralBackground1: figmaVariables.colors.grey.white,
  colorNeutralBackground1Hover: figmaVariables.colors.grey[96],
  colorNeutralBackground1Pressed: figmaVariables.colors.grey[88],
  colorNeutralBackground1Selected: figmaVariables.colors.grey[92],
  // Neutral / Background / 2
  colorNeutralBackground2: figmaVariables.colors.grey[98],
  colorNeutralBackground2Hover: figmaVariables.colors.grey[94],
  colorNeutralBackground2Pressed: figmaVariables.colors.grey[86],
  colorNeutralBackground2Selected: figmaVariables.colors.grey[90],
  // Neutral / Background / 3
  colorNeutralBackground3: figmaVariables.colors.grey[96],
  colorNeutralBackground3Hover: figmaVariables.colors.grey[92],
  colorNeutralBackground3Pressed: figmaVariables.colors.grey[84],
  colorNeutralBackground3Selected: figmaVariables.colors.grey[88],
  // Neutral / Background / 4
  colorNeutralBackground4: figmaVariables.colors.grey[94],
  colorNeutralBackground4Hover: figmaVariables.colors.grey[98],
  colorNeutralBackground4Pressed: figmaVariables.colors.grey[96],
  colorNeutralBackground4Selected: figmaVariables.colors.grey.white,
  // Neutral / Background / 5
  colorNeutralBackground5: figmaVariables.colors.grey[92],
  colorNeutralBackground5Hover: figmaVariables.colors.grey[96],
  colorNeutralBackground5Pressed: figmaVariables.colors.grey[94],
  colorNeutralBackground5Selected: figmaVariables.colors.grey[98],
  // Neutral / Background / 5
  colorNeutralBackground6: figmaVariables.colors.grey[90],
  // Neutral / Inverted
  colorNeutralBackgroundInverted: figmaVariables.colors.grey[16],
  // Neutral / Inverted / Disabled
  colorNeutralBackgroundInvertedDisabled: figmaVariables.colors.grey[94],
  // Neutral / Background / Alpha 1
  colorNeutralBackgroundAlpha: "rgba(255, 255, 255, 0.5)",
  // Neutral / Background / Alpha 2
  colorNeutralBackgroundAlpha2: "rgba(255, 255, 255, 0.8)",
  // Neutral / Background / Static
  colorNeutralBackgroundStatic: figmaVariables.colors.grey[20],
  // Neutral / Background / Subtle
  colorSubtleBackground: figmaVariables.colors.grey.white,
  colorSubtleBackgroundHover: figmaVariables.colors.grey[96],
  colorSubtleBackgroundPressed: figmaVariables.colors.grey[88],
  colorSubtleBackgroundSelected: figmaVariables.colors.grey[92],
  // Neutral / Background / Subtle / Light alpha
  colorSubtleBackgroundLightAlphaHover: "rgba(255, 255, 255, 0.7)",
  colorSubtleBackgroundLightAlphaPressed: "rgba(255, 255, 255, 0.5)",
  colorSubtleBackgroundLightAlphaSelected: "rgba(255, 255, 255, 0)",
  // Neutral / Background / Subtle / Inverted
  colorSubtleBackgroundInverted: "rgba(255, 255, 255, 0)",
  colorSubtleBackgroundInvertedHover: "rgba(0, 0, 0, 0.1)",
  colorSubtleBackgroundInvertedPressed: "rgba(0, 0, 0, 0.3)",
  colorSubtleBackgroundInvertedSelected: "rgba(0, 0, 0, 0.2)",
  // Neutral / Background / Transparent
  colorTransparentBackground: "rgba(255, 255, 255, 0)",
  colorTransparentBackgroundHover: "rgba(255, 255, 255, 0)",
  colorTransparentBackgroundPressed: "rgba(255, 255, 255, 0)",
  colorTransparentBackgroundSelected: "rgba(255, 255, 255, 0)",
  // Neutral / Background / Disabled
  colorNeutralBackgroundDisabled: figmaVariables.colors.grey[94],
  // Neutral / Stencil / 1
  colorNeutralStencil1: figmaVariables.colors.grey[90],
  // Neutral / Stencil / 2
  colorNeutralStencil2: figmaVariables.colors.grey[98],
  // Neutral / Background / Overlay
  colorBackgroundOverlay: "rgba(0, 0, 0, 0.4)",
  // Neutral / Scrollbar / Overlay
  colorScrollbarOverlay: "rgba(0, 0, 0, 0.5)",
  // Neutral / Foreground / 1
  colorNeutralForeground1: figmaVariables.colors.grey[14],
  colorNeutralForeground1Hover: figmaVariables.colors.grey[14],
  colorNeutralForeground1Pressed: figmaVariables.colors.grey[14],
  colorNeutralForeground1Selected: figmaVariables.colors.grey[14],
  // Neutral / Foreground / 2
  colorNeutralForeground2: figmaVariables.colors.grey[26],
  colorNeutralForeground2Hover: figmaVariables.colors.grey[14],
  colorNeutralForeground2Pressed: figmaVariables.colors.grey[14],
  colorNeutralForeground2Selected: figmaVariables.colors.grey[14],
  // Neutral / Foreground / 2 / Brand
  colorNeutralForeground2BrandHover: figmaVariables.colors.brand[80],
  colorNeutralForeground2BrandPressed: figmaVariables.colors.grey[70],
  colorNeutralForeground2BrandSelected: figmaVariables.colors.grey[80],
  // Neutral / Foreground / 2 / Link
  colorNeutralForeground2Link: figmaVariables.colors.grey[26],
  colorNeutralForeground2LinkHover: figmaVariables.colors.grey[14],
  colorNeutralForeground2LinkPressed: figmaVariables.colors.grey[14],
  colorNeutralForeground2LinkSelected: figmaVariables.colors.grey[14],
  // Neutral / Foreground / 3
  colorNeutralForeground3: figmaVariables.colors.grey[38],
  colorNeutralForeground3Hover: figmaVariables.colors.grey[26],
  colorNeutralForeground3Pressed: figmaVariables.colors.grey[26],
  colorNeutralForeground3Selected: figmaVariables.colors.grey[26],
  // Neutral / Foreground / 2 / Brand
  colorNeutralForeground3BrandHover: figmaVariables.colors.brand[80],
  colorNeutralForeground3BrandPressed: figmaVariables.colors.grey[70],
  colorNeutralForeground3BrandSelected: figmaVariables.colors.grey[80],
  // Neutral / Foreground / 4
  colorNeutralForeground4: figmaVariables.colors.grey[44],
  // Neutral / Foreground / Disabled
  colorNeutralForegroundDisabled: figmaVariables.colors.grey[74],
  // Neutral / Foreground / Static
  colorNeutralForeground1Static: figmaVariables.colors.grey[14],
  // Neutral / Foreground / Static / Inverted
  colorNeutralForegroundStaticInverted: figmaVariables.colors.grey.white,
  // Neutral / Foreground / Inverted / Disabled
  colorNeutralForegroundInvertedDisabled: "rgba(255, 255, 255, 0.4)",
  // Neutral / Foreground / Inverted / 1
  colorNeutralForegroundInverted: figmaVariables.colors.grey.white,
  colorNeutralForegroundInvertedHover: figmaVariables.colors.grey.white,
  colorNeutralForegroundInvertedPressed: figmaVariables.colors.grey.white,
  colorNeutralForegroundInvertedSelected: figmaVariables.colors.grey.white,
  // Neutral / Foreground / Inverted / 2
  colorNeutralForegroundInverted2: figmaVariables.colors.grey.white,
  // Neutral / Foreground / Inverted / Link
  colorNeutralForegroundInvertedLink: figmaVariables.colors.grey.white,
  colorNeutralForegroundInvertedLinkHover: figmaVariables.colors.grey.white,
  colorNeutralForegroundInvertedLinkPressed: figmaVariables.colors.grey.white,
  colorNeutralForegroundInvertedLinkSelected: figmaVariables.colors.grey.white,
  // Neutral / Foreground / OnBrand
  colorNeutralForegroundOnBrand: figmaVariables.colors.grey.white,
  // Neutral / Stroke / 1
  colorNeutralStroke1: figmaVariables.colors.grey[82],
  colorNeutralStroke1Hover: figmaVariables.colors.grey[78],
  colorNeutralStroke1Pressed: figmaVariables.colors.grey[70],
  colorNeutralStroke1Selected: figmaVariables.colors.grey[74],
  // Neutral / Stroke / 2
  colorNeutralStroke2: figmaVariables.colors.grey[88],
  // Neutral / Stroke / 3
  colorNeutralStroke3: figmaVariables.colors.grey[94],
  // Neutral / Stroke / Alpha
  colorNeutralStrokeAlpha: "rgba(0, 0, 0, 0.05)",
  // Neutral / Stroke / Alpha / 2
  colorNeutralStrokeAlpha2: "rgba(255, 255, 255, 0.2)",
  // Neutral / Stroke / Subtle
  colorNeutralStrokeSubtle: figmaVariables.colors.grey[88],
  // Neutral / Stroke / OnBrand
  colorNeutralStrokeOnBrand: figmaVariables.colors.grey.white,
  // Neutral / Stroke / OnBrand / 2
  colorNeutralStrokeOnBrand2: figmaVariables.colors.grey.white,
  colorNeutralStrokeOnBrand2Hover: figmaVariables.colors.grey.white,
  colorNeutralStrokeOnBrand2Pressed: figmaVariables.colors.grey.white,
  colorNeutralStrokeOnBrand2Selected: figmaVariables.colors.grey.white,
  // Neutral / Stroke / Accessible
  colorNeutralStrokeAccessible: figmaVariables.colors.grey[38],
  colorNeutralStrokeAccessibleHover: figmaVariables.colors.grey[34],
  colorNeutralStrokeAccessiblePressed: figmaVariables.colors.grey[30],
  colorNeutralStrokeAccessibleSelected: figmaVariables.colors.brand[80],
  // Neutral / Stroke / Focus / 1
  colorStrokeFocus1: figmaVariables.colors.grey.white,
  // Neutral / Stroke / Focus / 2
  colorStrokeFocus2: figmaVariables.colors.brand[80],
  // Neutral / Stroke / Transparent
  colorTransparentStroke: "rgba(255, 255, 255, 0)",
  // Neutral / Stroke / Transparent / Interactive
  colorTransparentStrokeInteractive: "rgba(255, 255, 255, 0)",
  // Neutral / Stroke / Transparent / Disabled
  colorTransparentStrokeDisabled: "rgba(255, 255, 255, 0)",
  // Neutral / Stroke / Disabled
  colorNeutralStrokeDisabled: figmaVariables.colors.grey[88],
  // Neutral / Stroke / Disabled / Inverted
  colorNeutralStrokeDisabledInverted: "rgba(255, 255, 255, 0.4)",
  // - - - - - - - - - - - - - - -
  // - - - - - - Brand - - - - - -
  // - - - - - - - - - - - - - - -
  // Brand / Background / 1
  colorBrandBackground: figmaVariables.colors.brand[80],
  colorBrandBackgroundHover: figmaVariables.colors.brand[70],
  colorBrandBackgroundPressed: figmaVariables.colors.brand[50],
  colorBrandBackgroundSelected: figmaVariables.colors.brand[70],
  // Brand / Background / 2
  colorBrandBackground2: figmaVariables.colors.brand[160],
  colorBrandBackground2Hover: figmaVariables.colors.brand[150],
  colorBrandBackground2Pressed: figmaVariables.colors.brand[130],
  // Brand / Background / Inverted
  colorBrandBackgroundInverted: figmaVariables.colors.grey.white,
  colorBrandBackgroundInvertedHover: figmaVariables.colors.brand[160],
  colorBrandBackgroundInvertedPressed: figmaVariables.colors.brand[140],
  colorBrandBackgroundInvertedSelected: figmaVariables.colors.brand[150],
  // Brand / Background / Static
  colorBrandBackgroundStatic: figmaVariables.colors.brand[80],
  // Brand / Background / Compound
  colorCompoundBrandBackground1: figmaVariables.colors.brand[80],
  colorCompoundBrandBackground1Hover: figmaVariables.colors.brand[70],
  colorCompoundBrandBackground1Pressed: figmaVariables.colors.brand[60],
  // Brand / Foreground / 1
  colorBrandForeground: figmaVariables.colors.brand[80],
  // Brand / Foreground / 2
  colorBrandForeground2: figmaVariables.colors.brand[70],
  colorBrandForeground2Hover: figmaVariables.colors.brand[60],
  colorBrandForeground2Pressed: figmaVariables.colors.brand[30],
  // Brand / Foreground / Link
  colorBrandForegroundLink: figmaVariables.colors.brand[70],
  colorBrandForegroundLinkHover: figmaVariables.colors.brand[60],
  colorBrandForegroundLinkPressed: figmaVariables.colors.brand[40],
  colorBrandForegroundLinkSelected: figmaVariables.colors.brand[70],
  // Brand / Foreground / Inverted
  colorBrandForegroundInverted: figmaVariables.colors.brand[100],
  colorBrandForegroundInvertedHover: figmaVariables.colors.brand[110],
  colorBrandForegroundInvertedPressed: figmaVariables.colors.brand[100],
  // Brand / Foreground / OnLight
  colorBrandForegroundOnLight: figmaVariables.colors.brand[80],
  colorBrandForegroundOnLightHover: figmaVariables.colors.brand[70],
  colorBrandForegroundOnLightPressed: figmaVariables.colors.brand[50],
  colorBrandForegroundOnLightSelected: figmaVariables.colors.brand[60],
  // Brand / Foreground / Compound
  colorCompoundBrandForeground1: figmaVariables.colors.brand[80],
  colorCompoundBrandForeground1Hover: figmaVariables.colors.brand[70],
  colorCompoundBrandForeground1Pressed: figmaVariables.colors.brand[60],
  // Brand / Stroke / 1
  colorBrandStroke1: figmaVariables.colors.brand[80],
  // Brand / Stroke / 2
  colorBrandStroke2: figmaVariables.colors.brand[140],
  colorBrandStroke2Hover: figmaVariables.colors.brand[120],
  colorBrandStroke2Pressed: figmaVariables.colors.brand[80],
  // Brand / Stroke / 2 / Contrast
  colorBrandStroke2Contrast: figmaVariables.colors.brand[140],
  // Brand / Stroke / Compound
  colorCompoundBrandStroke: figmaVariables.colors.brand[80],
  colorCompoundBrandStrokeHover: figmaVariables.colors.brand[70],
  colorCompoundBrandStrokePressed: figmaVariables.colors.brand[60],
  // - - - - - - - - - - - - - - - -
  // - - - - - - Status - - - - - -
  // - - - - - - - - - - - - - - - -
  // Status / Danger / Background / 1
  colorStatusDangerBackground1: "#FDF3F4",
  // Status / Danger / Background / 3
  colorStatusDangerBackground3: "#C50F1F",
  // Status / Danger / Foreground / 1
  colorStatusDangerForeground1: "#B10E1C",
  // Status / Danger / Foreground / 3
  colorStatusDangerForeground3: "#D13438",
  // Status / Danger / Stroke / 1
  colorStatusDangerBorder1: "#EEACB2",
  // Status / Danger / Stroke / 2
  colorStatusDangerBorder2: "#C50F1F",
  // Status / Success / Background / 1
  colorStatusSuccessBackground1: "#F1FAF1",
  // Status / Success / Background / 3
  colorStatusSuccessBackground3: "#107C10",
  // Status / Success / Foreground / 1
  colorStatusSuccessForeground1: "#0E700E",
  // Status / Success / Foreground / 3
  colorStatusSuccessForeground3: "#107C10",
  // Status / Success / Foreground / Inverted
  colorStatusSuccessForegroundInverted: "#359B35",
  // Status / Success / Stroke / 1
  colorStatusSuccessBorder1: "#9FD89F",
  // Status / Success / Stroke / 2
  colorStatusSuccessBorder2: "#107C10",
  // Status / Warning / Background / 1
  colorStatusWarningBackground1: "#FFF9F5",
  // Status / Warning / Background / 3
  colorStatusWarningBackground3: "#F7630C",
  // Status / Warning / Foreground / 1
  colorStatusWarningForeground1: "#BC4B09",
  // Status / Warning / Foreground / 2
  colorStatusWarningForeground3: "#8A3707",
  // Status / Warning / Stroke / 1
  colorStatusWarningBorder1: "#FDCFB4",
  // - - - - - - - - - - - - - - - -
  // - - - - - - Graph - - - - - - -
  // - - - - - - - - - - - - - - - -
  g10: figmaVariables.colors.graph[10],
  g20: figmaVariables.colors.graph[20],
  g30: figmaVariables.colors.graph[30],
  g40: figmaVariables.colors.graph[40],
  g50: figmaVariables.colors.graph[50],
  g60: figmaVariables.colors.graph[60]
};

// src/gql/queryMetricValue.ts
import { gql } from "@apollo/client";
var QUERY_METRIC_VALUE = gql`
  subscription queryMetricValue {
    queryMetricValue {
      entityIDmetricID
      entityID
      metricID
      sensorID
      timestamp
      value
    }
  }
`;

// src/twin-templates/eventTwinTemplate.ts
import { DataScope } from "@repo/frontend-types";
var eventTwinTemplate = {
  physicalEntities: ["event", "station", "operator"],
  filterEntities: ["event", "ticketCategory", "ticketType"],
  maria: {
    id: "analytics",
    introPrompts: []
  },
  timeline: {
    id: "timeline",
    visible: true,
    open: true,
    order: 1,
    interval: "15m"
  },
  calendar: {
    id: "calendar",
    visible: true,
    icon: "",
    order: 2,
    weekView: {
      dataScope: DataScope.ACTIVE_HOURS
    },
    monthView: {
      dataScope: DataScope.ACTIVE_DAYS
    }
  }
};

// src/twin-templates/expoTwinTemplate.ts
import { DataScope as DataScope2 } from "@repo/frontend-types";
var expoTwinTemplate = {
  physicalEntities: ["site", "area", "asset", "floor", "wall"],
  maria: {
    id: "maria",
    introPrompts: []
  },
  timeline: {
    id: "timeline",
    visible: true,
    open: true,
    order: 1,
    interval: "15m"
  },
  calendar: {
    id: "calendar",
    visible: true,
    open: true,
    icon: "",
    order: 2,
    weekView: {
      dataScope: DataScope2.ACTIVE_HOURS
    },
    monthView: {
      dataScope: DataScope2.ACTIVE_DAYS
    }
  }
};

// src/twin-templates/gymTwinTemplate.ts
import { DataScope as DataScope3 } from "@repo/frontend-types";
var gymTwinTemplate = {
  physicalEntities: ["site", "area", "asset", "floor", "wall"],
  maria: {
    id: "maria",
    introPrompts: []
  },
  timeline: {
    id: "timeline",
    visible: true,
    open: true,
    order: 1,
    interval: "15m"
  },
  calendar: {
    id: "calendar",
    visible: true,
    open: true,
    icon: "",
    order: 2,
    weekView: {
      dataScope: DataScope3.ACTIVE_HOURS
    },
    monthView: {
      dataScope: DataScope3.ACTIVE_DAYS
    }
  }
};
export {
  QUERY_METRIC_VALUE,
  dataColors,
  entitySelectionPrompts,
  eventTwinTemplate,
  excludedTypes,
  expoTwinTemplate,
  figmaVariables,
  getStackedChartColors,
  gymTwinTemplate,
  lightTheme,
  loadingMessageData,
  onboardingSteps,
  weekDays
};
